import { Alert, Button, Checkbox, Group, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { HumanDecisionGateStep, HumanDecisionResponseField } from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import StepCommonFields from '@/pages/Editor/components/SharedFields/StepCommonFields.tsx';
import MultiDataInputSelector from '@/pages/Editor/components/SharedFields/DataInputSelector/MultiDataInputSelector/MultiDataInputSelector.tsx';
import StepAdvancedConfigFields from '@/pages/Editor/components/SharedFields/StepAdvancedConfigFields.tsx';

type Props = { step: HumanDecisionGateStep };

export const HumanDecisionGateForm = ({ step }: Props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<HumanDecisionGateStep>({
    step,
    syncWithAPI: true,
  });

  const updateResponseField = (
    index: number,
    patch: Partial<HumanDecisionResponseField>,
    immediate = false,
  ) => {
    const updated = { ...fields.response_fields[index], ...patch };
    if (updated.field_type !== 'enum') updated.enum_values = [];
    const response_fields = fields.response_fields.map((field, fieldIndex) =>
      fieldIndex === index ? updated : field,
    );
    (immediate ? onChangeNoDelay : onChange)({ response_fields });
  };

  const addResponseField = () => {
    const fieldNumber = fields.response_fields.length + 1;
    onChangeNoDelay({ response_fields: [...fields.response_fields, { field_id: `response_${fieldNumber}`, label: `Response ${fieldNumber}`, field_type: 'text', required: false, enum_values: [], max_length: 500 }] });
  };

  const removeResponseField = () => {
    if (fields.response_fields.length > 1) onChangeNoDelay({ response_fields: fields.response_fields.slice(0, -1) });
  };

  return (
    <Stack gap="md">
      <StepCommonFields step={step} nameLabel="Step Name" />
      <Alert color="blue" variant="light" title="Typed human decision">
        <Text size="sm">The upstream request must explicitly set status to <code>review_required</code>.</Text>
      </Alert>
      <MultiDataInputSelector step={step} label="Decision request input" />
      <Text fw={600} size="sm">Reviewer response fields</Text>
      {fields.response_fields.map((responseField, index) => <Stack key={`${responseField.field_id}-${index}`} gap="xs" p="xs" style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4 }}>
        <Text size="sm" fw={500}>Response field {index + 1}</Text>
        <TextInput label="Field id" value={responseField.field_id} onChange={(event) => updateResponseField(index, { field_id: event.currentTarget.value })} />
        <TextInput label="Label" value={responseField.label} onChange={(event) => updateResponseField(index, { label: event.currentTarget.value })} />
        <Select label="Response type" value={responseField.field_type} data={['enum', 'text', 'boolean', 'number', 'date']} onChange={(value) => value && updateResponseField(index, { field_type: value as HumanDecisionResponseField['field_type'] }, true)} />
        {responseField.field_type === 'enum' && <TextInput label="Allowed choices" description="Comma-separated values reviewers may select." value={responseField.enum_values.join(', ')} onChange={(event) => updateResponseField(index, { enum_values: event.currentTarget.value.split(',').map((item) => item.trim()).filter(Boolean) })} />}
        <Checkbox label="Response is required" checked={responseField.required} onChange={(event) => updateResponseField(index, { required: event.currentTarget.checked }, true)} />
      </Stack>)}
      <Group><Button size="xs" variant="light" onClick={addResponseField}>Add response field</Button><Button size="xs" variant="subtle" color="red" disabled={fields.response_fields.length === 1} onClick={removeResponseField}>Remove last field</Button></Group>
      <Checkbox label="Require reviewer reason" checked={fields.require_reason} onChange={(event) => onChangeNoDelay({ require_reason: event.currentTarget.checked })} />
      <Select label="Staleness policy" value={fields.expiry_mode} data={[
        { value: 'expire', label: 'Expire when stale' },
        { value: 'resume_without_refresh', label: 'Resume without refresh' },
        { value: 'refresh_read_only_dependencies', label: 'Refresh read-only dependencies' },
        { value: 'refresh_then_require_reapproval', label: 'Refresh then require reapproval' },
      ]} onChange={(value) => value && onChangeNoDelay({ expiry_mode: value as HumanDecisionGateStep['expiry_mode'] })} />
      <TextInput label="Read-only refresh step IDs" description="Comma-separated step IDs. Required only for a refresh policy; enter them before selecting one. Reapproval refreshes one safe upstream path; refresh-before-effects targets safe downstream dependencies." value={fields.refresh_step_ids.join(', ')} onChange={(event) => onChange({ refresh_step_ids: event.currentTarget.value.split(',').map((value) => value.trim()).filter(Boolean) })} />
      <NumberInput label="Expiry hours" description="How long the request remains open before it is terminalized." min={1} max={720} value={fields.expiry_hours ?? ''} onChange={(value) => onChange({ expiry_hours: typeof value === 'number' ? value : null })} />
      <StepAdvancedConfigFields step={step} />
    </Stack>
  );
};
